import { useState, useCallback, useEffect, useRef } from "react";

interface UseDealersSearchReturn {
  searchValue: string;
  searchInputValue: string;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setCurrentPage: (page: number) => void;
}

export const useDealersSearch = (
  setCurrentPageCallback: (page: number) => void
): UseDealersSearchReturn => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const searchTimeoutRef = useRef<number | null>(null);

  // Debounced search handler
  const debouncedSearch = useCallback(
    (value: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = window.setTimeout(() => {
        setSearchValue(value);
        setCurrentPageCallback(1); // Reset to first page when searching
      }, 500); // 500ms debounce delay
    },
    [setCurrentPageCallback]
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const setCurrentPage = (page: number) => {
    setCurrentPageCallback(page);
  };

  return {
    searchValue,
    searchInputValue,
    handleSearchChange,
    setCurrentPage,
  };
};
